import { computed } from '@nuxtjs/composition-api';
import type { Ref } from '@nuxtjs/composition-api';
import { useVSFContext } from '@vue-storefront/core';
import { useAbsoluteUrlPage, useI18n } from '~/composables';
import { AbsoluteUrlPage } from '~/types/components/AbsoluteUrl/AbsoluteUrlPage';
import type { DynamicPageContent } from '~/types/cms/DynamicPageContent';
import { hasNativeRouteRedirection } from '~/helpers/navigation/nativeRoutingRedirection';
import { SEO_REDIRECTION_CODE } from '~/constants/seo';
import { buildCategoryPageUrl } from '~/helpers/category/buildCategoryPageUrl';
import { addNativeRoutingToProductUrl } from '~/helpers/routes/nativeRouting/transformProductsForNativeRouting';
import isStringTrue from '~/helpers/dataStructure/isStringTrue';

const useNativeRoute = (
  absoluteUrlSlug: string,
  content: Ref<DynamicPageContent>,
) => {
  const { currentPage } = useAbsoluteUrlPage(content);
  const { redirect, route } = useVSFContext();
  const { countryCode } = useI18n();

  const isRedirectionNeeded = computed(() => hasNativeRouteRedirection(countryCode.value));

  const makeCategoryRedirectionIfNeeded = (): boolean => {
    let isRedirectionMade = false;
    if (isRedirectionNeeded.value) {
      redirect(
        SEO_REDIRECTION_CODE,
        buildCategoryPageUrl({
          category: absoluteUrlSlug,
          countryCode: countryCode.value,
        }),
        route.value?.query,
      );
      isRedirectionMade = true;
    }
    return isRedirectionMade;
  };

  const makePDPRedirectionIfNeeded = (): boolean => {
    let isRedirectionMade = false;
    if (isRedirectionNeeded.value) {
      redirect(
        SEO_REDIRECTION_CODE,
        addNativeRoutingToProductUrl(absoluteUrlSlug, countryCode.value),
        route.value?.query,
      );
      isRedirectionMade = true;
    }
    return isRedirectionMade;
  };

  const makeNativeRouteRedirectionIfNeeded = (): boolean => {
    let isRedirectionMade = false;
    if (isStringTrue(process.env.ACTIVATE_NATIVE_AND_ABSOLUTE_PATHS_BOTH)) {
      return isRedirectionMade;
    }
    if (currentPage.value === AbsoluteUrlPage.pdp) {
      isRedirectionMade = makePDPRedirectionIfNeeded();
    } else if (currentPage.value === AbsoluteUrlPage.categorySearch) {
      isRedirectionMade = makeCategoryRedirectionIfNeeded();
    }
    return isRedirectionMade;
  };

  return {
    makeNativeRouteRedirectionIfNeeded,
  };
};

export default useNativeRoute;
